import { GiftingQuotaCounter } from '@/components/subscription/plan-usage/GiftingQuotaCounter';
import { useQuotaUpgrade } from '@/components/subscription/plan-usage/use-quota-upgrade';
import {
  giftingInvitationsLeft,
  useGiftingInvitationQuota,
} from '@/hooks/queries/use-gifting-invitation-quota';
import { Stack, Text } from '@chakra-ui/react';
import { PlanLimit, type BriefTemplateContent, type PlanQuotaMeta } from '@fluenceur/types';
import { useTranslation } from 'react-i18next';

const UNCOUNTED: PlanQuotaMeta = { used: 0, limit: null, resetsAt: null };

/**
 * Whether this send fits the month's gifting invitations. A proposal offering a
 * product is a gifting invitation; on a plan that counts them, a send larger
 * than what is left is not sent, the plan choice opens instead.
 */
export function useCollabGiftingAllowance(
  content: BriefTemplateContent | null | undefined,
  recipientCount: number
) {
  const quota = useGiftingInvitationQuota();
  const upgrade = useQuotaUpgrade(PlanLimit.GIFTING_INVITATIONS, quota ?? UNCOUNTED);
  const isGifting = content?.compensation.type === 'gifted_product';
  const left = isGifting ? giftingInvitationsLeft(quota) : null;

  return {
    quota: isGifting && quota && quota.limit !== null ? quota : null,
    left,
    blocked: left !== null && recipientCount > left,
    upgrade,
  };
}

/** The month's counter, and why a larger selection is not sent. */
export const CollabGiftingAllowance = ({
  quota,
  left,
  selected,
}: {
  quota: PlanQuotaMeta;
  left: number;
  selected: number;
}) => {
  const { t } = useTranslation();
  return (
    <Stack gap="1.5">
      <GiftingQuotaCounter quota={quota} />
      {left > 0 && selected > left && (
        <Text fontSize="12.5px" color="warning.text" lineHeight="1.5">
          {t('invitations.modal.collab.wizard.send.giftingOverSelection', { selected, left })}
        </Text>
      )}
    </Stack>
  );
};
